import { FC, useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { FormattedMessage, useIntl } from "react-intl";
import { MapContainer } from "react-leaflet";
import { useParams } from "react-router-dom";
import { compareAsc, parse } from "date-fns";
import moment from "moment";

import { useGetTrackingReferences } from "api/queries/shipments/useGetTrackingReferences";
import { useGetShipmentBasic } from "api/queries/shipments/useGetShipmentBasic";
import { useGetDeliveryStopsPod } from "api/queries/shipments/useGetDeliveryStopsPod";
import getLabel from "helpers/getLabel";
import { ReactComponent as ShipmentExceptionIcon } from "assets/images/iconsV2/shipment-exception.svg";
import { ReactComponent as EditIcon } from "assets/images/iconsV2/edit.svg";
import { ReactComponent as PlusIcon } from "assets/images/iconsV2/plus.svg";
import LoadingSkeleton from "components/atoms/LoadingSkeleton/LoadingSkeleton";
import Button from "components/v2/Button/Button";
import Card from "components/v2/Card/Card";
import TrackingMap from "pages/shipper/ShipmentTracking/components/TrackingMap/TrackingMap";
import TruckUtils from "pages/shipper/ShipmentTracking/components/TrackingMap/TruckUtils";
import Badge, { BadgeColor } from "components/v2/Badge/Badge";
import { capitalizeFirstLetter } from "helpers/commonHelpers";
import Divider from "components/v2/Divider/Divider";
import EmptyState from "components/v2/EmptyState/EmptyState";
import { ReactComponent as FeaturedIcon } from "assets/images/iconsV2/featured-icon-gray.svg";
import { getBadgeTranslations } from "helpers/translationHelpers";
import { FEATURE_FLAGS } from "constants/featureFlags";
import { ShipmentTypes } from "constants/";
import useFeatureFlag from "hooks/useFeatureFlag";
import EditBookingReferenceModal from "./EditBookingReferenceModal";
import { resolveOverviewShipmentStatusDisplay } from "./partialDeliveryStatus";

/**
 * Get the first non-null date in an array of dates
 * @param dates - An array of dates
 * @returns The first non-null date in the array, or "N/A" if all dates are null
 */
const getFirstNonNullDate = (...dates: (string | null)[]): string => dates.find((date) => date) || "N/A";

/**
 * Format a date and time string
 * @param date - The date string
 * @param time - The time string
 * @returns The formatted date and time string
 */
const formatDateTime = (date?: string, time?: string) => {
  if (date && time) {
    return moment(`${date} ${time}`).format("DD MMM YYYY, HH:mm");
  }
  if (date) return moment(date).format("DD MMM yyyy");
  return null;
};

/**
 * Get the color style for a date based on a reference date
 * @param referenceDate - The reference date
 * @param compareDate - The date to compare
 * @returns The color style
 */
const getDateColorStyle = (referenceDate: string | null, compareDate: string | null): string => {
  if (!compareDate || !referenceDate) return "";

  const reference = parse(referenceDate, "dd MMM yyyy, HH:mm", new Date());
  const compare = parse(compareDate, "dd MMM yyyy, HH:mm", new Date());

  const comparison = compareAsc(compare, reference);

  if (comparison > 0) return "text-warning-primary"; // Later
  if (comparison < 0) return "text-success-primary"; // Earlier
  return ""; // Same
};

const badgeStatusColors: Record<string, BadgeColor> = {
  Booked: "brand",
  "In transit": "brand",
  Delayed: "warning",
  Delivered: "success",
  Canceled: "error",
  Incomplete: "warning",
  "Auto-closed": "error",
};

const PARTIAL_DELIVERY_BADGE_COLOR: BadgeColor = "success";

type OverviewProps = {
  /**
   * The shipment data.
   */
  shipment: any;
  /**
   * The route data.
   */
  route: any;
  /**
   * Whether the shipment data is loading.
   */
  isShipmentLoading: boolean;
  /**
   * Whether the route data is loading.
   */
  isRouteLoading: boolean;
  /**
   * Whether the shipment is off-platform.
   */
  isOffPlatform: boolean;
  /**
   * Called after a successful booking reference update so the parent can refetch.
   */
  onBookingReferenceUpdated?: () => void;
};

const Overview: FC<OverviewProps> = function (props) {
  const { isShipmentLoading, isRouteLoading, shipment, route, isOffPlatform, onBookingReferenceUpdated } = props;
  const intl = useIntl();
  const { id } = useParams();
  const [isHoveringBookingRef, setIsHoveringBookingRef] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const { organization, service, schedule } = shipment;
  const { shipment_status, has_exception } = organization ?? {};
  const { freight_mode_type, freight_mode_label, freight_mode } = service ?? {};
  const {
    actual_pickup_date,
    actual_pickup_time,
    quoted_pickup_date,
    quoted_pickup_time,
    stop1_quoted_delivery_date,
    stop1_quoted_delivery_time,
    stop1_actual_delivery_date,
    stop1_actual_delivery_time,
    proof_of_pickup_date,
    proof_of_pickup_time,
    address_v2,
    number_of_stops,
  } = schedule ?? {};

  const pickup_city = address_v2?.pickup_city ?? schedule?.pickup_city;
  const pickup_state = address_v2?.pickup_state ?? schedule?.pickup_state;
  const pickup_country = address_v2?.pickup_country ?? schedule?.pickup_country;

  const stop1_city = address_v2?.delivery_city ?? schedule?.stop1_city;
  const stop1_state = address_v2?.delivery_state ?? schedule?.stop1_state;
  const stop1_country = address_v2?.delivery_country ?? schedule?.stop1_country;

  const freightMode = `${capitalizeFirstLetter(freight_mode_type ?? "")} ${freight_mode_type ? "•" : ""} ${
    freight_mode_label ?? freight_mode ?? ""
  }`;

  const pickupLocation = `${pickup_city}${pickup_state ? `, ${pickup_state}` : ""}, ${pickup_country}`;
  const deliveryLocation = `${stop1_city}${stop1_state ? `, ${stop1_state}` : ""}, ${stop1_country}`;

  const bookedPickupDate = formatDateTime(quoted_pickup_date, quoted_pickup_time);
  const bookedDeliveryDate = formatDateTime(stop1_quoted_delivery_date, stop1_quoted_delivery_time);

  const actualPickupDate = formatDateTime(actual_pickup_date, actual_pickup_time);
  const actualDeliveryDate = formatDateTime(stop1_actual_delivery_date, stop1_actual_delivery_time);

  const proofOfPickupDate = formatDateTime(proof_of_pickup_date, proof_of_pickup_time);

  const pickupDate = getFirstNonNullDate(proofOfPickupDate, actualPickupDate, bookedPickupDate);
  const deliveryDate = getFirstNonNullDate(actualDeliveryDate, bookedDeliveryDate);

  const pickupDateColor = getDateColorStyle(bookedPickupDate, proofOfPickupDate || actualPickupDate);
  const deliveryDateColor = getDateColorStyle(bookedDeliveryDate, actualDeliveryDate);

  const { data: trackingReferencesData, isFetching: isFetchingTrackingReferencesData } = useGetTrackingReferences(id!, {
    enabled: !!id,
    refetchOnMount: false,
  });
  const { data: shipmentBasicData }: any = useGetShipmentBasic(id!, {
    enabled: !!id,
    refetchOnMount: false,
  });
  const { payment_location_reference } = shipmentBasicData?.organization ?? {};
  const isMultistop = shipmentBasicData?.shipment?.shipment_type === ShipmentTypes.MULTISTOP;
  const isRevampEnabled = useFeatureFlag(FEATURE_FLAGS.WEB_11070_TRACKING_NUMBER_REVAMP);
  const isMultistopPodEnabled = useFeatureFlag(FEATURE_FLAGS.WEB_10959_MULTISTOP_POD);
  const isMultistopPodShipment = isMultistop && isMultistopPodEnabled;
  const { data: deliveryStopsPodData } = useGetDeliveryStopsPod(id!, {
    enabled: !!id && isMultistopPodShipment,
    refetchOnMount: false,
  });

  const statusDisplay = resolveOverviewShipmentStatusDisplay({
    isMultistop: isMultistopPodShipment,
    backendStatus: shipment_status,
    stops: deliveryStopsPodData?.stops,
  });
  const statusBadgeColor =
    statusDisplay.kind === "partial" ? PARTIAL_DELIVERY_BADGE_COLOR : badgeStatusColors[shipment_status];
  const statusBadgeLabel =
    statusDisplay.kind === "partial"
      ? intl.formatMessage(
          {
            id: "general.partiallyDelivered",
            defaultMessage: "Partially Delivered ({delivered}/{total})",
          },
          {
            delivered: statusDisplay.deliveredCount,
            total: statusDisplay.totalCount,
          },
        )
      : getBadgeTranslations(shipment_status, intl) ?? shipment_status;

  const bookingReference = shipment?.organization?.booking_reference?.booking_reference_value;
  const bookingReferenceLabel = getLabel("Booking reference", "capitalize-first", payment_location_reference);
  const isBookingReferenceSettingEnabled =
    !!trackingReferencesData &&
    !isFetchingTrackingReferencesData &&
    trackingReferencesData.tracking_settings?.booking_reference_enabled !== "off";
  const showRevampBookingReferenceRow = isRevampEnabled && isBookingReferenceSettingEnabled;
  const showLegacyBookingReferenceRow = !isRevampEnabled && !!bookingReference;
  const shouldShowBookingReferenceRow = showRevampBookingReferenceRow || showLegacyBookingReferenceRow;
  const hasTruckTrackingData = trackingReferencesData?.references?.some(
    ({ truck_tracking_info }: any = {}) =>
      truck_tracking_info?.status === "active" && truck_tracking_info?.locations?.length > 0,
  );

  return (
    <div>
      <Card borderRadius="md">
        <Card.Header title={intl.formatMessage({ id: "general.overview", defaultMessage: "Overview" })} showBorder />
        {isShipmentLoading && <LoadingSkeleton rows={1} />}
        {!isShipmentLoading && Object.keys(shipment).length === 0 && (
          <Card.Body>
            <EmptyState
              noActions
              customIcon={<FeaturedIcon />}
              title={intl.formatMessage({
                id: "general.couldNotLoadDetails",
                defaultMessage: "We couldn't load these details",
              })}
              description={intl.formatMessage({
                id: "general.tryAgainOrCheckBackLater",
                defaultMessage: "You can try again or check back later.",
              })}
            />
          </Card.Body>
        )}
        {!isShipmentLoading && Object.keys(shipment).length > 0 && (
          <>
            <Card.LineItem
              label={intl.formatMessage({ id: "general.status", defaultMessage: "Status" })}
              rightContent={
                <div className="d-flex align-items-center gap-lg">
                  <Badge
                    color={statusBadgeColor}
                    label={statusBadgeLabel}
                    type="status-badge"
                    variant="dot"
                  />
                  {has_exception && (
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id="shipment-tip">
                          <span className="font-weight-normal">
                            <FormattedMessage id="general.exception" defaultMessage="Exception" />
                          </span>
                        </Tooltip>
                      }
                    >
                      <ShipmentExceptionIcon />
                    </OverlayTrigger>
                  )}
                </div>
              }
            />

            <div className="pl-3xl">
              <Divider />
            </div>

            <Card.LineItem
              label={intl.formatMessage({ id: "general.freightMode", defaultMessage: "Freight Mode" })}
              rightContent={<span className="font-medium text-sm">{freightMode}</span>}
            />

            {number_of_stops > 0 && (
              <>
                <div className="pl-3xl">
                  <Divider />
                </div>
                <Card.LineItem
                  label={intl.formatMessage({ id: "general.#OfStops", defaultMessage: "" })}
                  rightContent={<span className="font-medium text-sm">{number_of_stops}</span>}
                />
              </>
            )}

            {!isOffPlatform && (
              <>
                <div className="pl-3xl">
                  <Divider />
                </div>
                <Card.LineItem
                  label={intl.formatMessage({ id: "general.pickup", defaultMessage: "Pickup" })}
                  rightContent={
                    <div className="d-flex flex-column text-right">
                      <div className={`font-medium text-sm ${pickupDateColor}`}>{pickupDate}</div>
                      <div className="font-regular text-sm">{pickupLocation}</div>
                      <div className="font-regular text-xs text-tertiary">
                        <FormattedMessage id="general.quoted" defaultMessage="Quoted" />: {bookedPickupDate}
                      </div>
                    </div>
                  }
                />
                <div className="pl-3xl">
                  <Divider />
                </div>
                <Card.LineItem
                  label={intl.formatMessage({ id: "general.delivery", defaultMessage: "Delivery" })}
                  rightContent={
                    <div className="d-flex flex-column text-right">
                      <div className={`font-medium text-sm ${deliveryDateColor}`}>{deliveryDate}</div>
                      <div className="font-regular text-sm">{deliveryLocation}</div>

                      <div className="font-regular text-xs text-tertiary">
                        <FormattedMessage id="general.quoted" defaultMessage="Quoted" />: {bookedDeliveryDate}
                      </div>
                    </div>
                  }
                />
              </>
            )}
            {shouldShowBookingReferenceRow && (
              <div onMouseEnter={() => setIsHoveringBookingRef(true)} onMouseLeave={() => setIsHoveringBookingRef(false)}>
                <div className="pl-3xl">
                  <Divider />
                </div>
                <Card.LineItem
                  label={bookingReferenceLabel}
                  rightContent={
                    showRevampBookingReferenceRow && !bookingReference ? (
                      <Button
                        variant="link-color"
                        label={intl.formatMessage({ id: "general.add", defaultMessage: "Add" })}
                        iconLeft={<PlusIcon />}
                        onClick={() => setIsEditModalOpen(true)}
                      />
                    ) : (
                      <div className="d-flex align-items-center gap-lg">
                        <span className="font-medium text-sm">{bookingReference}</span>
                        {isHoveringBookingRef && (
                          <EditIcon className="cursor-pointer" onClick={() => setIsEditModalOpen(true)} />
                        )}
                      </div>
                    )
                  }
                />
              </div>
            )}
            {id && (
              <EditBookingReferenceModal
                isOpen={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                shipmentId={id}
                currentValue={bookingReference ?? ""}
                label={bookingReferenceLabel}
                onSuccess={() => onBookingReferenceUpdated?.()}
              />
            )}
            {!isOffPlatform && !isFetchingTrackingReferencesData && (
              <Card.Accordion
                accordionTitle={(isOpen) =>
                  isOpen
                    ? intl.formatMessage({ id: "tracking.hideMap", defaultMessage: "Hide map" })
                    : intl.formatMessage({ id: "tracking.showMap", defaultMessage: "Show map" })
                }
                defaultOpened={hasTruckTrackingData}
              >
                <Card.Body>
                  {isRouteLoading && <LoadingSkeleton rows={1} />}
                  {!isRouteLoading && (
                    <MapContainer style={{ height: "100%", zIndex: 1, borderRadius: "14px" }} attributionControl={false}>
                      <TrackingMap data={shipment} shipmentRoute={route} />
                    </MapContainer>
                  )}
                  <TruckUtils />
                </Card.Body>
              </Card.Accordion>
            )}
          </>
        )}
      </Card>
    </div>
  );
};

export default Overview;
